/**
 * Controller Abstraction Layer
 * 
 * Defines a common interface for all mobile control schemes
 * allowing easy switching between different input methods.
 */

/**
 * Action that can be performed on the falling pair
 */
export type GameAction = 
  | 'moveLeft'
  | 'moveRight'
  | 'rotateClockwise'
  | 'rotateCounterClockwise'
  | 'softDrop'
  | 'hardDrop'
  | 'pause'
  | 'purify'

/**
 * Action with timestamp for telemetry
 */
export interface ActionEvent {
  action: GameAction
  timestamp: number
}

/**
 * Controller interface that all input schemes must implement
 */
export interface IController {
  /**
   * Enable the controller and start listening for input
   */
  onEnable(): void

  /**
   * Disable the controller and stop listening for input
   */
  onDisable(): void

  /**
   * Update controller state (called each frame)
   * @param dtMs Delta time in milliseconds
   */
  update(dtMs: number): void

  /**
   * Get buffered actions since last call
   * Returns array of actions in order they were triggered
   */
  readonly actions: RingBuffer<ActionEvent>

  /**
   * Get the scheme name for telemetry
   */
  readonly schemeName: string

  /**
   * Enable or disable haptic feedback
   */
  setHapticEnabled(enabled: boolean): void

  /**
   * Enable or disable debug visualization
   */
  setDebugMode(enabled: boolean): void

  /**
   * Check if debug mode is enabled
   */
  isDebugMode(): boolean

  /**
   * Toggle debug mode and return new state
   */
  toggleDebugMode(): boolean

  /**
   * Resize handler for responsive layout
   */
  resize(width: number, height: number): void

  /**
   * Cleanup resources
   */
  destroy(): void
}

/**
 * Ring Buffer for efficient action queuing
 * Prevents memory leaks by maintaining a fixed-size circular buffer
 */
export class RingBuffer<T> {
  private buffer: (T | undefined)[]
  private head: number = 0
  private tail: number = 0
  private size: number = 0
  private capacity: number

  constructor(capacity: number = 32) {
    this.capacity = capacity
    this.buffer = new Array(capacity)
  }

  /**
   * Add item to the buffer
   * Overwrites oldest item if buffer is full
   */
  push(item: T): void {
    this.buffer[this.tail] = item
    this.tail = (this.tail + 1) % this.capacity

    if (this.size < this.capacity) {
      this.size++
    } else {
      // Buffer is full, advance head
      this.head = (this.head + 1) % this.capacity
    }
  }

  /**
   * Remove and return oldest item from buffer
   * Returns undefined if buffer is empty
   */
  shift(): T | undefined {
    if (this.size === 0) {
      return undefined
    }

    const item = this.buffer[this.head]
    this.buffer[this.head] = undefined
    this.head = (this.head + 1) % this.capacity
    this.size--

    return item
  }

  /**
   * Get all items and clear the buffer
   * Returns items in order from oldest to newest
   */
  drain(): T[] {
    const items: T[] = []
    let item = this.shift()
    while (item !== undefined) {
      items.push(item)
      item = this.shift()
    }
    return items
  }

  /**
   * Check if buffer is empty
   */
  isEmpty(): boolean {
    return this.size === 0
  }

  /**
   * Get current number of items in buffer
   */
  getSize(): number {
    return this.size
  }

  /**
   * Clear all items from buffer
   */
  clear(): void {
    this.buffer = new Array(this.capacity)
    this.head = 0
    this.tail = 0
    this.size = 0
  }
}
